import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Divider, type DrawerProps, Grid, IconButton, Stack, Typography, styled } from "@mui/material";
import { Drawer, DrawerContent } from "Components/Analyses/_Shared/Drawer";
import { Button } from "Components/_Shared/Buttons/Button";
import { ReactComponent as CloseIcon } from "Components/_Shared/Icons/Assets/close.svg";
import { FormDropdown, type Option } from "Forms/Components/FormDropdown";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import { type DrawerIncomeFormType, getDrawerIncomeFormSchema } from "Forms/Schemas/Budget/Incomes/drawerIncomeFormSchema";
import { type AnalysisClientItem, IncomeIncomeType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatClientName, isPlannedChild } from "Infrastructure/Utils/ClientUtils";
import { format } from "date-fns";
import { type FC, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { DisabilityIncomeForm } from "../Forms/DisabilityIncomeForm";
import { EmploymentIncomeForm } from "../Forms/EmploymentIncomeForm";
import { OrphanOrWidowIncomeForm } from "../Forms/OrphanOrWidowIncomeForm";
import { OtherIncomeForm } from "../Forms/OtherIncomeForm";
import { ParentalIncomeForm } from "../Forms/ParentalIncomeForm";
import { PensionIncomeForm } from "../Forms/PensionIncomeForm";
import { RentalIncomeForm } from "../Forms/RentalIncomeForm";
import { SelfEmploymentIncomeForm } from "../Forms/SelfEmploymentIncomeForm";
import { IncomeTypeTile } from "./IncomeTypeTile";

type Props = {
  onClose: () => void;
  clients?: AnalysisClientItem[];
} & DrawerProps;

export type SubmitIncomeFormHandle = {
  readonly submitForm: () => void;
};

export const AddIncomeDrawer: FC<Props> = ({ onClose, clients, ...rest }) => {
  const { t } = useResource();
  const [incomeType, setIncomeType] = useState<IncomeIncomeType>(IncomeIncomeType.employment);

  const employmentIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);
  const selfEmploymentIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);
  const rentalIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);
  const parentalIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);
  const pensionIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);
  const disabilityIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);
  const orphanOrWidowIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);
  const otherIncomeFormRef = useRef<SubmitIncomeFormHandle>(null);

  const clientOptions: Option[] = (clients ?? [])
    .filter((client) => !isPlannedChild(client))
    .map((client) => ({
      value: client.guid,
      name: formatClientName(client.firstName, client.surname, client.birthDate ?? null),
    }));

  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<DrawerIncomeFormType>({
    resolver: yupResolver(getDrawerIncomeFormSchema(t)),
    mode: "onBlur",
    defaultValues: {
      clientId: "",
    },
  });

  const selectedClientId = watch("clientId");

  const handleClose = () => {
    setValue("clientId", "");
    onClose();
  };

  return (
    <Drawer {...rest} onClose={handleClose}>
      <Box display="flex" justifyContent="space-between" alignItems="center" p={Spacing.XXL}>
        <Typography variant="h2">{t(Resources.AnalysisDetail.Budget.Incomes.Add)}</Typography>

        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DrawerContent>
        <Box px={Spacing.XXL} mb={Spacing.XXL}>
          <FormDropdown
            control={control}
            name="clientId"
            labelEndAdornment={<StyledLabelAdornment />}
            label={t(Resources.AnalysisDetail.Budget.Incomes.IncomeBelongsTo)}
            errors={errors}
            options={clientOptions}
          />

          <Divider />
        </Box>

        <Box px={Spacing.XXL} mb={Spacing.XXL}>
          <Stack spacing={Spacing.XL}>
            <Typography variant="subtitle1" fontWeight={600}>
              {t(Resources.AnalysisDetail.Budget.Incomes.IncomeType)}
            </Typography>

            <Grid container spacing={Spacing.M}>
              {(Object.values(IncomeIncomeType) as Array<IncomeIncomeType>).map((value) => {
                return (
                  <Grid key={value} size={3}>
                    <IncomeTypeTile
                      active={value === incomeType}
                      incomeType={value}
                      onIncomeSelect={(type) => {
                        setIncomeType(type);
                      }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Stack>
        </Box>

        <Box px={Spacing.XXL} mb={Spacing.XXL}>
          {incomeType === IncomeIncomeType.employment && (
            <EmploymentIncomeForm onClose={handleClose} clientId={selectedClientId} ref={employmentIncomeFormRef} />
          )}
          {incomeType === IncomeIncomeType.self_employment && (
            <SelfEmploymentIncomeForm onClose={handleClose} clientId={selectedClientId} ref={selfEmploymentIncomeFormRef} />
          )}
          {incomeType === IncomeIncomeType.rental && (
            <RentalIncomeForm onClose={handleClose} clientId={selectedClientId} ref={rentalIncomeFormRef} />
          )}
          {incomeType === IncomeIncomeType.parental && (
            <ParentalIncomeForm onClose={handleClose} clientId={selectedClientId} ref={parentalIncomeFormRef} />
          )}
          {incomeType === IncomeIncomeType.pension && (
            <PensionIncomeForm onClose={handleClose} clientId={selectedClientId} ref={pensionIncomeFormRef} />
          )}
          {incomeType === IncomeIncomeType.disability && (
            <DisabilityIncomeForm onClose={handleClose} clientId={selectedClientId} ref={disabilityIncomeFormRef} />
          )}
          {incomeType === IncomeIncomeType.orphan_or_widow_pension && (
            <OrphanOrWidowIncomeForm onClose={handleClose} clientId={selectedClientId} ref={orphanOrWidowIncomeFormRef} />
          )}
          {incomeType === IncomeIncomeType.other && (
            <OtherIncomeForm onClose={handleClose} clientId={selectedClientId} ref={otherIncomeFormRef} />
          )}
        </Box>
      </DrawerContent>

      <Box display="flex" flexDirection="row" justifyContent="flex-end" py={Spacing.XXL} px={Spacing.XXL}>
        <Button
          onClick={handleClose}
          color="secondary"
          sx={{
            marginRight: Spacing.XL,
          }}
        >
          {t(Resources.Common.Close)}
        </Button>

        <Button
          disabled={!selectedClientId}
          type="submit"
          onClick={() => {
            if (incomeType === IncomeIncomeType.employment) {
              employmentIncomeFormRef.current?.submitForm();
            }
            if (incomeType === IncomeIncomeType.self_employment) {
              selfEmploymentIncomeFormRef.current?.submitForm();
            }
            if (incomeType === IncomeIncomeType.rental) {
              rentalIncomeFormRef.current?.submitForm();
            }
            if (incomeType === IncomeIncomeType.parental) {
              parentalIncomeFormRef.current?.submitForm();
            }
            if (incomeType === IncomeIncomeType.pension) {
              pensionIncomeFormRef.current?.submitForm();
            }
            if (incomeType === IncomeIncomeType.disability) {
              disabilityIncomeFormRef.current?.submitForm();
            }
            if (incomeType === IncomeIncomeType.orphan_or_widow_pension) {
              orphanOrWidowIncomeFormRef.current?.submitForm();
            }
            if (incomeType === IncomeIncomeType.other) {
              otherIncomeFormRef.current?.submitForm();
            }
          }}
        >
          {t(Resources.Common.Create)}
        </Button>
      </Box>
    </Drawer>
  );
};
